<script setup lang="ts">
import { useForm } from 'vee-validate'
import { z } from 'zod'
import { toTypedSchema } from '@vee-validate/zod'

const { t } = useI18n()
const { $posthog, $consent } = useNuxtApp()

const projectTypes = [
  t('contact.form.project_types.marketplace'),
  t('contact.form.project_types.mobile_app'),
  t('contact.form.project_types.mvp'),
  t('contact.form.project_types.process_automation'),
  t('contact.form.project_types.product_discovery_workshop'),
  t('contact.form.project_types.web_application'),
  t('contact.form.other'),
]

const budgetTypes = ['15.000€ - 25.000€', '25.000€ - 50.000€', '50.000€ - 100.000€', '100.000€ - 200.000€', '200.000€+']

const referrerTypes = [
  t('contact.form.referrer_types.friend'),
  'LinkedIn',
  'Google',
  'Clutch',
  'Goodfirms',
  'Feedbax',
  'TechBehemoths',
  t('contact.form.other'),
]

const formSchema = z
  .object({
    email: z
      .string()
      .nonempty(t('contact.form.validations.email.required'))
      .email(t('contact.form.validations.email.format')),
    name: z.string().nonempty(t('contact.form.validations.name.required')),
    company: z.string().nonempty(t('contact.form.validations.company.required')),
    subject: z.string().nonempty(t('contact.form.validations.required')),
    message: z.string().nonempty('This field is required.'),
    alias: z.string().nullish(),

    budget: z.string().optional(),
    projectType: z.string().optional(),
    projectTypeOther: z.string().optional(),
    referrer: z.string().optional(),
    referrerOther: z.string().optional(),
  })
  .superRefine((schema, ctx) => {
    if (schema.projectType === 'Other' && !schema.projectTypeOther) {
      ctx.addIssue({
        code: 'custom',
        message: t('contact.form.validations.required'),
        path: ['projectTypeOther'],
      })
    }
    if (schema.referrer === 'Other' && !schema.referrerOther) {
      ctx.addIssue({
        code: 'custom',
        message: t('contact.form.validations.required'),
        path: ['referrerOther'],
      })
    }
  })

type Form = z.infer<typeof formSchema>

const { values, resetForm, handleSubmit } = useForm({
  validationSchema: toTypedSchema(formSchema),
})
const onSubmit = handleSubmit((values) => {
  sendEmail(values)
})

const buttonLabel = ref<string>(t('contact.form.button.submit'))
const sendEmail = async (values: Form) => {
  buttonLabel.value = t('contact.form.button.sending')

  try {
    await fetch('/contact', {
      method: 'post',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        name: values.name,
        message: values.message,
        email: values.email,
        subject: values.subject,
        company: values.company,
        budget: values.budget,
        alias: values.alias,
        project_type: values.projectTypeOther || values.projectType,
        referrer: values.referrerOther || values.referrer,
      }),
    })

    if ($consent('analytics')) {
      $posthog.identify(values.email, {
        email: values.email,
      })
    }

    buttonLabel.value = t('contact.form.button.success')
    resetForm()
    setTimeout(() => {
      buttonLabel.value = t('contact.form.button.submit')
    }, 2000)
  } catch (err) {
    buttonLabel.value = t('contact.form.button.error')
  }
}
</script>

<template>
  <form novalidate class="bordershadow-lg-gray space-y-4 rounded-lg bg-white p-8 md:p-12" @submit="onSubmit">
    <div class="space-y-2">
      <Heading>{{ $t('contact.form.title') }}</Heading>
      <Paragraph>
        {{ $t('contact.form.description') }}
      </Paragraph>
    </div>

    <div class="no-flex-xs flex w-full space-x-4">
      <InputField
        required
        name="name"
        :label="$t('contact.form.fields.name.label')"
        :placeholder="$t('contact.form.fields.name.placeholder')"
        class="no-flex-xs flex-1"
      />

      <InputField
        required
        name="company"
        :label="$t('contact.form.fields.company.label')"
        :placeholder="$t('contact.form.fields.company.placeholder')"
        class="no-flex-xs flex-1"
      />
      <InputField
        name="alias"
        label="Alias"
        class="sr-only"
        tabindex="-1"
        autocomplete="off"
        aria-label="Please don't fill this field in."
      />
    </div>

    <InputField
      required
      name="email"
      :label="$t('contact.form.fields.email.label')"
      :placeholder="$t('contact.form.fields.email.placeholder')"
      type="email"
    />

    <InputField
      required
      name="subject"
      :label="$t('contact.form.fields.subject.label')"
      :placeholder="$t('contact.form.fields.subject.placeholder')"
    />
    <TextareaField
      required
      name="message"
      :label="$t('contact.form.fields.message.label')"
      :placeholder="$t('contact.form.fields.message.placeholder')"
    />

    <hr />

    <Paragraph>
      {{ $t('contact.optionals') }}
    </Paragraph>

    <div class="no-flex-xs grid grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-x-4 gap-y-4">
      <SelectField
        name="projectType"
        :label="$t('contact.form.fields.project_type.label')"
        :placeholder="$t('contact.form.select_one')"
        class="no-flex-xs flex-1"
        :options="projectTypes"
      />

      <SelectField
        name="budget"
        :label="$t('contact.form.fields.budget.label')"
        :placeholder="$t('contact.form.select_one')"
        class="no-flex-xs flex-1"
        :options="budgetTypes"
      />
    </div>

    <InputField
      v-if="values.projectType === t('contact.form.other')"
      name="projectTypeOther"
      :label="$t('contact.form.fields.project_type_other.label')"
      :placeholder="$t('contact.form.fields.project_type_other.placeholder')"
      class="no-flex-xs flex-1"
    />

    <SelectField
      name="referrer"
      :label="$t('contact.form.fields.referrer.label')"
      :placeholder="$t('contact.form.select_one')"
      :options="referrerTypes"
    />

    <InputField
      v-if="values.referrer === t('contact.form.other')"
      name="referrerOther"
      :label="$t('contact.form.fields.referrer_other.label')"
      :placeholder="$t('contact.form.fields.referrer_other.placeholder')"
    />

    <div class="flex w-full justify-end pt-4">
      <Action intent="main" type="submit">
        {{ buttonLabel }}
      </Action>
    </div>
  </form>
</template>
